import React, { useState } from "react";

import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import AlertDialog from "@/src/components/dialog/AlertDialog";
import { Button } from "@/src/components/ui/button";
import { cn } from "@/src/lib/utils";

import { CONTROL_SURFACE_CLASS } from "./dataTableStyles";

import type { JSX } from "react";

import type { BulkAction } from "./dataTable.types";

interface DataTableBulkActionsProps<TData> {
  actions: BulkAction<TData>[];
  rows: TData[];
  disabled?: boolean;
  onClearSelection: () => void;
}

function DataTableBulkActions<TData>({
  actions,
  rows,
  disabled = false,
  onClearSelection,
}: DataTableBulkActionsProps<TData>): JSX.Element {
  const { t } = useTranslation("table");
  const [pendingAction, setPendingAction] = useState<BulkAction<TData> | null>(
    null,
  );

  const run = (action: BulkAction<TData>): void => {
    action.onClick(rows);
    onClearSelection();
  };

  const handleSelect = (action: BulkAction<TData>): void => {
    if (action.confirm) {
      setPendingAction(action);
      return;
    }
    run(action);
  };

  const handleConfirm = (): void => {
    if (pendingAction) run(pendingAction);
    setPendingAction(null);
  };

  const handleConfirmOpenChange = (isOpen: boolean): void => {
    if (!isOpen) setPendingAction(null);
  };

  return (
    <React.Fragment>
      <span className="text-muted-foreground text-sm">
        {t("dataTable.selectedCount", { count: rows.length })}
      </span>
      <Button onClick={onClearSelection} size="lg" variant="ghost">
        {t("dataTable.clearSelection")}
        <X />
      </Button>
      {actions.map((action) => (
        <Button
          key={action.label}
          disabled={disabled}
          onClick={() => handleSelect(action)}
          size="lg"
          variant={action.variant === "destructive" ? "destructive" : "outline"}
          className={cn(
            action.variant !== "destructive" && CONTROL_SURFACE_CLASS,
          )}
        >
          {action.icon}
          {action.label}
        </Button>
      ))}
      <AlertDialog
        cancelLabel={t("dataTable.cancel")}
        confirmVariant={pendingAction?.variant}
        description={pendingAction?.confirm?.description}
        isOpen={pendingAction?.confirm !== undefined}
        onConfirm={handleConfirm}
        onOpenChange={handleConfirmOpenChange}
        title={pendingAction?.confirm?.title}
        confirmLabel={
          pendingAction?.confirm?.confirmLabel ?? t("dataTable.confirm")
        }
      />
    </React.Fragment>
  );
}

export default DataTableBulkActions;
